import { yupResolver } from '@hookform/resolvers/yup'
import { Ring } from '@uiball/loaders'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import FileChip from 'components/UI/Chips/FileChip'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import FileInput from 'components/UI/Inputs/FileInput'
import Typography from 'components/UI/Typography'
import PassportBlock from 'components/views/DigitalDoc/PassportBlock'
import RetryDdocs from 'components/views/DigitalDoc/RetryDdocs'
import DigitalDocSkeleton from 'components/views/DigitalDoc/Skeleton'
import OtpVerification from 'components/views/OtpVerification'
import { useAppDispatch, useAppSelector } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGetApplicationStatusQuery, useLazyGetApplicationStatusQuery } from 'redux/api'
import { useLazyGetUnsignedDocumentsQuery } from 'redux/api/creditApi'
import {
  useDdocsResendOtpMutation,
  useDdocsVerifyMutation,
  useLazyStartDdocsQuery,
  useManualDdocsUploadMutation,
  useStartDdocsQuery
} from 'redux/api/ddocsApi'
import { getActiveCreditApplication, getCurrentFlowId } from 'redux/store/slices/userSlice'
import {
  getNotificationsSelector,
  getServerNotificationSelector,
  removeStepNotificationData
} from 'redux/store/slices/wsSlice'
import TokenService from 'services/token.service'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS } from 'share/types'
import tw from 'twin.macro'
import { getWsStepStatus } from 'utils'
import { baseUrl } from 'utils/constants'
import Emitter from 'utils/emitter'
import * as yup from 'yup'

type OtpSchemaValues = {
  code: string
}

const lang = localStorage.getItem('i18nextLng')
const otpSchema = yup.object({
  code: yup
    .string()
    .min(6, `${lang === 'ru' ? 'Код должен быть не менее 6 символов' : 'Код кем дегенде 6 таңбадан тұруы керек'}`)
    .required('Заполните это поле пожалуйста')
})

const MAX_EGOV_ATTEMPTS = 1

const DigitalDoc = () => {
  // Tools
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const dispatch = useAppDispatch()

  // Form
  const { control: otpControl, setError } = useForm<OtpSchemaValues>({
    defaultValues: {
      code: ''
    },
    mode: 'onChange',
    resolver: yupResolver(otpSchema)
  })

  // Selectors
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const currentNotifyValue = useAppSelector(getServerNotificationSelector)
  const { FETCH_DDOCS } = useAppSelector(getNotificationsSelector)
  const { id: creditID } = useAppSelector(getActiveCreditApplication)

  // State
  const [docFile, setDocFile] = useState<File | undefined>(undefined)
  const [isDdocsValidating, setIsDdocsValidating] = useState(false)
  const [documentUrl, setDocumentUrl] = useState<string | undefined>(undefined)
  const [isDocumentLoading, setIsDocumentLoading] = useState(false)
  // Queries
  const {
    data: statusData,
    isSuccess: isStatusSuccess,
    refetch: refetchStatus
  } = useGetApplicationStatusQuery(currentFlowId as string, {
    skip: !currentFlowId
  })

  const fetchDdocsSkipper = useMemo(() => {
    if (!isStatusSuccess || docFile) return true
    const { current_step: currentStep, next_step } = statusData

    if (next_step === FLOW_STEPS.FETCH_DDOCS) return false
    if (!currentStep) return true

    if (currentStep.step === FLOW_STEPS.FETCH_DDOCS) {
      if (['IN_PROGRESS', 'DONE'].includes(currentStep.status) || currentStep.count_attempts >= MAX_EGOV_ATTEMPTS)
        return true
      else if (currentStep.can_retry && currentStep.status === 'FAILED') return false
    } else if (statusData.next_step === FLOW_STEPS.FETCH_DDOCS) {
      return false
    }

    return true
  }, [docFile, isStatusSuccess, statusData])

  useStartDdocsQuery(currentFlowId as string, {
    skip: fetchDdocsSkipper
  })

  // Mutations
  const [fetchStatus, { isLoading: isStatusLoading }] = useLazyGetApplicationStatusQuery()
  const [startFetchingDdocs, { isLoading: isStartDdocsLoading }] = useLazyStartDdocsQuery()
  const [fetchDdocsVerify, { isLoading: isDdocsVerifyLoading }] = useDdocsVerifyMutation()
  const [fetchResendDdocsOtp] = useDdocsResendOtpMutation()
  const [fetchDigitalDocs, { isLoading: isDdocsFetching }] = useLazyGetUnsignedDocumentsQuery()
  const [manualDdocsUpload, { isError: isManualDdocsUploadError, isLoading: isMaunalUploadLoading }] =
    useManualDdocsUploadMutation()

  // Computed variables
  const isOtpNeeded = useMemo(() => {
    if (!statusData || !FETCH_DDOCS) return undefined
    const currentStep = statusData.current_step
    if (!currentStep) return undefined
    const fetchDdocsStatusData = currentStep.data as { is_otp?: boolean } | null
    const ddocsWsData = FETCH_DDOCS.value.data as { is_otp?: boolean } | null
    return fetchDdocsStatusData?.is_otp ?? ddocsWsData?.is_otp
  }, [FETCH_DDOCS, statusData])

  const isManualUploadAvailable = useMemo(() => {
    if (!statusData) return false
    const currentStep = statusData.current_step
    if (!currentStep) return false
    return currentStep.step === FLOW_STEPS.FETCH_DDOCS && currentStep.count_attempts >= MAX_EGOV_ATTEMPTS
  }, [statusData])

  const fetchDdocsStepStatus = useMemo(() => getWsStepStatus(FETCH_DDOCS), [FETCH_DDOCS])

  const isFetchDdocsFailed = useMemo(
    () =>
      fetchDdocsStepStatus === 'RETRY' ||
      (statusData?.current_step?.step === FLOW_STEPS.FETCH_DDOCS && statusData.current_step.status === 'FAILED'),
    [fetchDdocsStepStatus, statusData]
  )

  // Handlers
  const handleOtpVerify = async (code: string | undefined) => {
    try {
      if (!currentFlowId) return
      const { current_step } = await fetchStatus(currentFlowId).unwrap()
      if (!current_step) return
      await fetchDdocsVerify({ step_uuid: current_step.id, code }).unwrap()
    } catch (error) {
      setError('code', { type: 'custom', message: 'asdas' })
    }
  }

  const handleOtpResend = async () => {
    try {
      if (!currentFlowId) return
      const { current_step } = await fetchStatus(currentFlowId).unwrap()
      if (!current_step) return
      await fetchResendDdocsOtp({ step_uuid: current_step.id })
    } catch (err) {
      console.log(err)
    }
  }

  const handleRetryDdocs = async () => {
    try {
      if (!currentFlowId) return
      await startFetchingDdocs(currentFlowId)
      await refetchStatus()
      dispatch(removeStepNotificationData(FLOW_STEPS.FETCH_DDOCS))
    } catch (error) {
      console.log(error)
    }
  }

  const uploadDdocsManually = async () => {
    try {
      if (!docFile || !creditID || !currentFlowId) return
      setIsDdocsValidating(true)
      const { current_step } = await fetchStatus(currentFlowId).unwrap()
      if (!current_step) return
      if (current_step.step !== FLOW_STEPS.FETCH_DDOCS) return

      const formData = new FormData()
      formData.append('borrower_id', docFile)
      await manualDdocsUpload({ body: formData, step_uuid: current_step.id }).unwrap()
    } catch (error) {
      console.log(error)
      setIsDdocsValidating(false)
    } finally {
      await refetchStatus().unwrap()
    }
  }

  const goNextPage = async () => {
    Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
  }

  // Effects
  useEffect(() => {
    const fetchDdocs = async () => {
      if (documentUrl || !statusData) return
      const currentStep = statusData.current_step
      if (!currentStep) return
      if (
        (currentStep.step === FLOW_STEPS.FETCH_DDOCS && currentStep.status === 'DONE') ||
        getWsStepStatus(FETCH_DDOCS) === 'DONE'
      ) {
        try {
          setIsDocumentLoading(true)
          setTimeout(async () => {
            const docArray = await fetchDigitalDocs(['digital_docs']).unwrap()
            const document = docArray.at(0)
            if (!document) return
            const documentBlob = await fetch(`${baseUrl}/credits/unsigned_documents/${document.id}`, {
              headers: {
                Authorization: `Bearer ${TokenService.getAccessToken()}`
              }
            }).then(res => res.blob())
            const documentUrl = URL.createObjectURL(documentBlob)
            setDocumentUrl(documentUrl)
          }, 2000)
        } catch (error) {
          console.log(error)
        } finally {
          setIsDocumentLoading(false)
        }
      } else if (currentStep.step === FLOW_STEPS.FETCH_DDOCS && currentStep.status === 'FAILED') {
        setIsDdocsValidating(false)
      }
    }

    fetchDdocs()
  }, [FETCH_DDOCS, documentUrl, fetchDigitalDocs, refetchStatus, statusData])

  useEffect(() => {
    const checkWsStep = async () => {
      const fetchDdocsStepStatus = getWsStepStatus(FETCH_DDOCS)
      if (fetchDdocsStepStatus === 'RETRY') {
        await refetchStatus().unwrap()
        setIsDdocsValidating(false)
      }
    }

    checkWsStep()
  }, [FETCH_DDOCS, refetchStatus])

  useEffect(() => {
    return () => {
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl)
      }
    }
  }, [documentUrl])

  if (documentUrl) {
    return (
      <ContainerWithSteps>
        <div tw='flex flex-col gap-y-9'>
          <PassportBlock url={documentUrl} />
          <div tw='px-4 sm:self-end sm:px-0'>
            <Button
              variant='primary'
              twStyle={tw`gap-x-4`}
              loading={isStatusLoading}
              disabled={!documentUrl}
              onClick={goNextPage}
            >
              <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
                {t('flow-next')}
              </Typography>
              <Typography variant='caption' twStyle={tw`text-white `}>
                {t('flow-go-to-sign')}
              </Typography>
            </Button>
          </div>
        </div>
      </ContainerWithSteps>
    )
  }

  return (
    <ContainerWithSteps>
      <InfoBlock twStyle={tw`p-0 mt-4 md:mt-0`}>
        {isFetchDdocsFailed && isManualUploadAvailable ? (
          <RetryDdocs isLoading={isStartDdocsLoading} onRetry={handleRetryDdocs} />
        ) : isOtpNeeded === undefined ? (
          <div className='ddocs-loading'>
            <DigitalDocSkeleton className='ddocs-skeleton' twStyle={tw`pb-0`} />
            <Typography tw='px-5 mt-4' weight='bold' className='ddocs-loading-alter-text'>
              {t('flow-please-wait')}
            </Typography>
          </div>
        ) : (
          <OtpVerification
            name='code'
            variant='dd-otp'
            twStyle={tw`pb-0`}
            showOtp={isOtpNeeded}
            otpLength={6}
            initSeconds={59}
            control={otpControl}
            disabled={isStartDdocsLoading || isDdocsVerifyLoading || isDdocsFetching || isDocumentLoading}
            onOtpVerify={handleOtpVerify}
            onOtpTimerReset={handleOtpResend}
            footerContent={
              currentNotifyValue && (
                <p tw='flex space-x-2'>
                  {!FETCH_DDOCS?.value.is_final && (
                    <span>
                      <Ring size={20} color='var(--primary-color)' />
                    </span>
                  )}
                  {currentNotifyValue && typeof currentNotifyValue === 'string' && (
                    <span tw='flex items-center space-x-2'>
                      <Typography as='span' twStyle={tw`text-grey-dark !text-s14`}>
                        {currentNotifyValue}
                      </Typography>
                    </span>
                  )}
                </p>
              )
            }
          />
        )}
        <div tw='px-5 my-4'>
          <hr tw='border-tertiary' />
        </div>
        <div tw='px-5 pb-5'>
          <Typography weight='bold'>{t('flow-manual-ddocs-upload')}</Typography>
          <Typography variant='subbody' twStyle={tw`text-grey-dark my-4`}>
            {t('confirm-with-doc')}
          </Typography>
          <FileInput
            disabled={isMaunalUploadLoading || docFile !== undefined}
            onChange={e => setDocFile(e.target.files?.[0])}
          />
          {docFile && (
            <div tw='mt-2'>
              <FileChip title={docFile.name} onRemoveChip={() => setDocFile(undefined)} />
            </div>
          )}

          {isManualDdocsUploadError ? (
            <Typography variant='subbody' twStyle={tw`mt-2 text-red-light`}>
              {t('ddocs-manual-upload-error')}
            </Typography>
          ) : !isDdocsValidating &&
            (getWsStepStatus(FETCH_DDOCS) === 'RETRY' ||
              (statusData && statusData.current_step && statusData.current_step.step === FLOW_STEPS.FETCH_DDOCS)) ? (
            <Typography variant='subbody' twStyle={tw`mt-2 text-red-light`}>
              {language === 'ru' ? statusData?.current_step?.status_reason : statusData?.current_step?.status_reason_kk}
            </Typography>
          ) : null}

          <Button
            variant='primary'
            twStyle={tw`mt-4`}
            disabled={!docFile}
            loading={isDdocsValidating}
            onClick={uploadDdocsManually}
          >
            <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
              {t('flow-continue')}
            </Typography>
            <Typography variant='caption' twStyle={tw`text-white `}>
              {t('flow-ddocs-continue')}
            </Typography>
          </Button>
        </div>
      </InfoBlock>
    </ContainerWithSteps>
  )
}
export default DigitalDoc
